import { SeverityLevel } from '@microsoft/applicationinsights-web';

import { WEB_VIEW } from 'shared/src/models/auth';
import { DESTINATION_APP_TYPE, MESSAGE_TYPE } from 'shared/src/composables/messages/messages.model';
import type { UseMessages } from 'shared/src/composables/messages/useMessages.composable';
import { useMessages } from 'shared/src/composables/messages/useMessages.composable';
import { CBI_SLANG_INITIAL_LANGUAGE, INITIAL_LANGUAGE } from 'shared/src/modules/languageModule/consts';
import { useLanguage } from 'shared/src/composables/useLanguage.composable';
import { sendExceptionEvent } from 'shared/src/utils/appInsights';
import { FeatureFlagName } from 'shared/clients/client-spa-ConfigurationGroup';
import { getThemeCookieDomain } from 'shared/src/helpers/themeCookieDomain';

import { setCbiLang, setLanguage } from '../language/langauge.service';
import { showLoaderText } from '../helpers';
import {
  loadDelegationOrganization,
  loadDelegationUser,
  loadIncorrectPerson,
  loadOrganization,
  loadUser,
  loadWT,
} from '../loaders';
import MobileAuthorizationService from './mobileAuthorization';
import StandardAuthorizationService from './standardAuthorization';
import type { AuthorizationResult } from './types';
import featureFlagService from '../featureFlag';
import { setTheme, THEME } from '../theme/theme';

const { storeLanguage } = useLanguage();

class AuthorizationStrategy {
  handleAuthorizationResult(result: AuthorizationResult, isWtUserFFActive: boolean = false) {
    const isDelegation = !!result.profile?.delegation_id;
    const isOrganization = !!result.profile?.orgid;
    const isWtUser = !!result.profile?.stp && result.profile.stp === 'Cbi';

    if (isDelegation) {
      this.loadDelegationContext(isOrganization);
    } else if (isWtUser && isWtUserFFActive) {
      this.loadWTContext();
    } else {
      this.loadDefaultContext(isOrganization);
    }
  }

  private loadDefaultContext(isOrganization: boolean): void {
    if (isOrganization) {
      loadOrganization();
    }
    else {
      try {
        loadUser();
      } catch (e) {
        console.log(e);
      }
    }
  }

  private loadDelegationContext(isOrganization: boolean): void {
    if (isOrganization) {
      loadDelegationOrganization();
    }
    else {
      loadDelegationUser();
    }
  }

  private loadWTContext(): void {
    try {
      loadWT();
    } catch (e) {
      console.log(e);
    }
  }
}

export class MobileAuthorizationStrategy extends AuthorizationStrategy {
  private useMessages: UseMessages;
  private mobileAuthService: MobileAuthorizationService;

  constructor() {
    super();
    this.mobileAuthService = new MobileAuthorizationService();
    this.useMessages = useMessages();
    this.useMessages.startMessageListener();
  }

  async authorize(): Promise<void | null> {
    this.useMessages.addOnMessageActionAlt(DESTINATION_APP_TYPE.HOST, MESSAGE_TYPE.REFRESH_TOKEN_INJECTION,
      async (refreshToken: string, messageEvent) => {
        if (refreshToken) {
          console.log('REFRESH_TOKEN_INJECTION', messageEvent);
          await this.mobileAuthService.decodeAndSetToken(refreshToken);
        }
      });

    this.useMessages.addOnMessageActionAlt(DESTINATION_APP_TYPE.HOST, MESSAGE_TYPE.LOGOUT_FROM_MOBILE_APP,
      async (messageEvent) => {
        console.log('LOGOUT USER FROM MOBILE APP', messageEvent);
        sessionStorage.clear();
        localStorage.clear();
      });

    const tokenReceived = async () =>  new Promise<string>((resolve, reject) => {
      this.useMessages.addOnMessageActionAlt(DESTINATION_APP_TYPE.HOST, MESSAGE_TYPE.TOKEN_INJECTION,
        async (token: string, messageEvent) => {
          console.log('TOKEN_INJECTION', messageEvent);
          if (token) {
            resolve(token);
          }
        });
    });

    const tokenAndLanguageReceived = async () => new Promise<{ token: string, language: string }>((resolve) => {
      this.useMessages.addOnMessageActionAlt(DESTINATION_APP_TYPE.HOST, MESSAGE_TYPE.TOKEN_AND_LANGUAGE_INJECTION,
        async ({ token, language }, messageEvent) => {
          console.log('TOKEN_AND_LANGUAGE_INJECTION', messageEvent);
          if (token && language) {
            resolve({ token, language });
          } else if (token) {
            resolve ({ token, language: INITIAL_LANGUAGE });
          }
        });
    });

    const tokenAndLanguageAndThemeReceived = async () =>
      new Promise<{ token: string, language: string, theme: THEME }>((resolve) => {
        this.useMessages.addOnMessageActionAlt(DESTINATION_APP_TYPE.HOST, MESSAGE_TYPE.TOKEN_AND_LANGUAGE_AND_THEME_INJECTION,
          async ({ token, language, theme }, messageEvent) => {
            console.log('TOKEN_AND_LANGUAGE_AND_THEME_INJECTION', messageEvent, 'theme: ', theme);
            if (token) {
              resolve({
                token,
                language: language ?? INITIAL_LANGUAGE,
                theme: theme ?? THEME.LIGHT,
              });
            }
          });
      });

    this.useMessages.addOnMessageActionAlt(DESTINATION_APP_TYPE.HOST, MESSAGE_TYPE.UPDATE_THEME,
      async (theme: THEME, messageEvent) => {
        if (theme) {
          console.log('THEME UPDATE', messageEvent, theme);
          setTheme(theme, getThemeCookieDomain());
        } else {
          setTheme(THEME.LIGHT, getThemeCookieDomain());
        }
      });

    this.useMessages.postWindowMessage(
      DESTINATION_APP_TYPE.MOBILE,
      MESSAGE_TYPE.WEB_INITIALIZED,
      null
    );

    const initialParamsReceived = async () => {
      return Promise.race([
        tokenReceived().then(token => ({ token, language: INITIAL_LANGUAGE, theme: THEME.LIGHT })),
        tokenAndLanguageReceived().then(({ token, language }) => ({ token, language, theme: THEME.LIGHT })),
        tokenAndLanguageAndThemeReceived(),
      ]);
    };

    const { token, language, theme } = await initialParamsReceived();
    sessionStorage.setItem(WEB_VIEW, 'true');
    storeLanguage(language);
    setTheme(theme, getThemeCookieDomain());

    const result = await this.mobileAuthService.decodeAndSetToken(token);

    return result ? this.handleAuthorizationResult(result) : null;
  }
}

export class StandardAuthorizationStrategy extends AuthorizationStrategy {
  private readonly authCode: string | null;
  constructor(authCode: string | null) {
    super();
    this.authCode = authCode;
  }

  async authorize(): Promise<void | null> {
    const authService = new StandardAuthorizationService();

    try {
      const result = await authService.authorize(this.authCode);
      if (!result) {
        // No auth code in URL — browser is already navigating to the IDP, nothing left to do.
        // Any async call here (e.g. fetchFeatureFlags) would throw NetworkError, land in the catch,
        // wipe orgId, and cause a second IDP redirect without org context — loading user context
        return null;
      }
      await featureFlagService.fetchFeatureFlags();

      const isWtUser = result ? !!result.profile?.stp && result.profile.stp === 'Cbi' : false;
      const isWtUserFFActive = featureFlagService.getFeatureFlagValue(FeatureFlagName.CountryNodeCrossBorder);
      const isCbiModuleActive = isWtUser && isWtUserFFActive;

      const initialAppText = result ? result.profile?.slng : isCbiModuleActive ? CBI_SLANG_INITIAL_LANGUAGE : undefined;

      showLoaderText(initialAppText);

      if (result?.profile) {
        if (isCbiModuleActive) {
          await setCbiLang(result.profile);
        } else {
          await setLanguage(result.profile);
        }
      }

      return result ? this.handleAuthorizationResult(result, isWtUserFFActive) : null;
    } catch (errorCode: any) {
      if (!sessionStorage.getItem('orgId')) {
        sendExceptionEvent({ exception: errorCode, severityLevel: SeverityLevel.Error, properties: { 'incorrect_person': true }});

        loadIncorrectPerson(errorCode);

        return;
      }
      sessionStorage.removeItem('orgId');

      return this.authorize();
    }
  }
}
